import { ApolloQueryResult } from "@apollo/client/core";
import { saveAs } from "file-saver";
import Encoding from "encoding-japanese";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import type { UseDeliveryRequestDownloadParams } from "../types";
import {
  DeliveryRequestDownloadInput,
  DownloadDeliveryRequestDocument,
  DownloadDeliveryRequestQuery,
  DownloadDeliveryRequestQueryVariables,
  OperationInput,
} from "@/generated/graphql";

const createLogInput = (): OperationInput => ({
  area_code: "10",
  detail: "操作詳細",
  display_category: "画面名",
  operation: "操作",
  user_id: localStorageGetUserId(),
});

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestDownload = ({
  downloadingDialog,
  overlay,
  errdialog,
  errMessage,
  listDataTotalCount,
  formatDateForDisplay,
  searchConditionReceptionNo,
  searchConditionMemberId,
  searchConditionName,
  searchConditionPhone,
  searchConditionZipCode,
  searchConditionRegisterNo,
  searchConditionReceiptNo,
  searchConditionReceptionDateFrom,
  searchConditionReceptionDateTo,
  searchConditionDeliveryDateFrom,
  searchConditionDeliveryDateTo,
  areaSelectValues,
  statusSelectedItem,
  searchConditionChainCode,
  searchConditionShopCode,
  authType,
  is_delivery_address1_display,
}: UseDeliveryRequestDownloadParams) => {
  const onClickDownload = async (): Promise<void> => {
    if (listDataTotalCount.value <= 0) {
      return;
    }

    downloadingDialog.value = true;
    try {
      const apolloClient = await generateApolloClient();
      const itemsPerPage = 100;
      const pageCount = Math.ceil(listDataTotalCount.value / itemsPerPage);
      let csvData = "";

      for (let page = 1; page <= pageCount; page++) {
        const input: DeliveryRequestDownloadInput = {
          reception_no: searchConditionReceptionNo.value || undefined,
          member_id: searchConditionMemberId.value
            ? Number(searchConditionMemberId.value)
            : undefined,
          name: searchConditionName.value || undefined,
          phone: searchConditionPhone.value || undefined,
          zip_code: searchConditionZipCode.value || undefined,
          register_no:
            searchConditionRegisterNo.value !== undefined &&
            searchConditionRegisterNo.value !== null &&
            searchConditionRegisterNo.value > 0
              ? searchConditionRegisterNo.value
              : undefined,
          receipt_no:
            searchConditionReceiptNo.value !== undefined &&
            searchConditionReceiptNo.value !== null &&
            searchConditionReceiptNo.value > 0
              ? searchConditionReceiptNo.value
              : undefined,
          reception_date_from:
            searchConditionReceptionDateFrom.value !== null
              ? formatDateForDisplay(searchConditionReceptionDateFrom.value)
              : undefined,
          reception_date_to:
            searchConditionReceptionDateTo.value !== null
              ? formatDateForDisplay(searchConditionReceptionDateTo.value)
              : undefined,
          delivery_date_from:
            searchConditionDeliveryDateFrom.value !== null
              ? formatDateForDisplay(searchConditionDeliveryDateFrom.value)
              : undefined,
          delivery_date_to:
            searchConditionDeliveryDateTo.value !== null
              ? formatDateForDisplay(searchConditionDeliveryDateTo.value)
              : undefined,
          area_code:
            areaSelectValues.value && areaSelectValues.value.area_code !== ""
              ? areaSelectValues.value.area_code
              : undefined,
          delivery_status:
            statusSelectedItem.value !== undefined &&
            statusSelectedItem.value !== null &&
            statusSelectedItem.value.length > 0
              ? statusSelectedItem.value.map((status) => String(status))
              : undefined,
          chain_code_list: searchConditionChainCode.value as unknown as string[],
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code_list:
            authType.value === "0"
              ? [localStorageGetShopCode() as string]
              : (searchConditionShopCode.value as unknown as string[]),
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          is_delivery_address1_display: is_delivery_address1_display.value,
        };

        let result: ApolloQueryResult<DownloadDeliveryRequestQuery>;
        try {
          result = await apolloClient.query<
            DownloadDeliveryRequestQuery,
            DownloadDeliveryRequestQueryVariables
          >({
            query: DownloadDeliveryRequestDocument,
            variables: {
              input,
              logInput: createLogInput(),
            },
            fetchPolicy: "no-cache",
          });
        } catch (err: unknown) {
          console.error(err);
          overlay.value = false;
          errdialog.value = true;
          errMessage.value = "配達依頼のダウンロードに失敗しました。";
          return;
        }

        if (!result.data.downloadDeliveryRequest) {
          console.error("result.data.downloadDeliveryRequest is undefined or null");
          return;
        }

        const json: { csv: string } = JSON.parse(result.data.downloadDeliveryRequest) as { csv: string };
        const binaryString = atob(json.csv);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const tempCsvData = new TextDecoder("utf-8")
          .decode(bytes)
          .replace(/^\uFEFF/, "");

        const lineArray = tempCsvData.split(/\r/);
        if (csvData === "") {
          const headerLineData = lineArray[0].split(/,/);
          headerLineData.splice(28, 1);
          csvData += `${headerLineData.join(",")}\n`;
        }

        for (let i = 1; i < lineArray.length - 1; i++) {
          const curLineData = lineArray[i].split(/,/);
          curLineData.splice(28, 1);
          csvData += `${curLineData.join(",")}\n`;
        }
      }

      const unicodeArray = Encoding.stringToCode(csvData);
      const sjisArray = Encoding.convert(unicodeArray, {
        to: "SJIS",
        from: "UNICODE",
      });
      const uint8Array = new Uint8Array(sjisArray);
      const blob = new Blob([uint8Array], {
        type: "text/csv",
      });
      saveAs(blob, "delivery_request.csv");
    } finally {
      downloadingDialog.value = false;
    }
  };

  return {
    onClickDownload,
  };
};
